import i18n from '@/i18n';

/** Клиент API: /api/* проксируется на FastAPI (vite proxy в dev, nginx в проде). Сессия — httpOnly cookie. */
const BASE = '/api';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message || code);
    this.status = status;
    this.code = code;
  }
}

async function parseError(r: Response): Promise<ApiError> {
  let code = r.statusText || `http_${r.status}`;
  try {
    const j = (await r.json()) as { detail?: unknown };
    if (typeof j.detail === 'string') code = j.detail;
    else if (j.detail) code = JSON.stringify(j.detail);
  } catch {
    /* не JSON */
  }
  return new ApiError(r.status, code);
}

type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;

async function request<T>(path: string, init?: FetchInit): Promise<T> {
  let r: Response;
  try {
    r = await fetch(BASE + path, {
      credentials: 'same-origin',
      ...init,
      headers: { 'Content-Type': 'application/json', 'Accept-Language': i18n.language, ...(init?.headers || {}) },
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  if (!r.ok) throw await parseError(r);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T,>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T,>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** multipart: файлы и поля формы; Content-Type ставит браузер */
  upload: async <T,>(path: string, fd: FormData): Promise<T> => {
    let r: Response;
    try {
      r = await fetch(BASE + path, { method: 'POST', body: fd, credentials: 'same-origin', headers: { 'Accept-Language': i18n.language } });
    } catch {
      throw new ApiError(0, 'network');
    }
    if (!r.ok) throw await parseError(r);
    return (await r.json()) as T;
  },
};

export type Me = {
  id: string;
  email: string;
  name: string;
  side: 'staff' | 'partner';
  role: string;
  locale: 'en' | 'ru';
  partner: { id: string; name: string; city: string | null; warehouse: string | null } | null;
  canApproveMargin: boolean;
  canApproveLoss: boolean;
  /** матрица прав роли (staff): разделы none/read/write и действия; null для партнёра */
  perms: { areas: Record<string, 'none' | 'read' | 'write'>; actions: Record<string, boolean> } | null;
};

export type FeatureFlags = {
  storefront: boolean;
  site_parse: boolean;
  web_orders: boolean;
  portal_upload: boolean;
  demand: boolean;
  warranty_ui: boolean;
};

export type PublicSettings = { featureFlags: FeatureFlags; version: string; googleConfigured: boolean; appUrl: string };

export const DEFAULT_FLAGS: FeatureFlags = { storefront: false, site_parse: false, web_orders: false, portal_upload: false, demand: false, warranty_ui: false };
